import { Clock, Palmtree } from 'lucide-react'
import { useEffect, useState, type KeyboardEvent, type ReactNode } from 'react'
import { EmptyState } from '@/components/ui/EmptyState'
import { Skeleton } from '@/components/ui/Skeleton'
import { toNumber } from '@/lib/decimal'
import { formatHmm } from '@/lib/hours'
import {
  usePickers,
  useSaveCell,
  useWeek,
  useWeekTotals,
  useWorkdayExceptions,
  type CellValues,
} from '@/lib/time-queries'
import type { DayTotal, Me, WeekRow, WorkdayException } from '@/lib/types'
import { cn } from '@/lib/utils'
import { dayStatus, formatDayLabel, weekMetaFromId } from '@/lib/week'
import { AddRowButton } from './AddRowButton'
import { closedOn, rowBoundary, rowMenuState, selectionFromRow, type NewRowSelection } from './week-rows'
import { CellPopover } from './CellPopover'
import { DayHeaderPopover } from './DayHeaderPopover'
import { Legend } from './Legend'
import { RowActions } from './RowActions'
import { RowLabelEditable } from './RowLabelEditable'
import { StoplichtDot } from './StoplichtDot'
import { TimesheetToolbar } from './TimesheetToolbar'
import { UtilizationStrip } from './UtilizationStrip'
import { WeekApprovalControl } from './WeekApprovalControl'
import { WeekNav, WeekTotalsStrip } from './WeekNav'
import type { TimesheetRows } from './useTimesheetRows'

interface WeekGridProps {
  weekId: string
  onWeekChange: (weekId: string) => void
  personId?: string
  me?: Me
  editable: boolean
  viewControl?: ReactNode
  /** Extra knoppen in het doen-cluster, naast Weekend en het week-akkoord. */
  actions?: ReactNode
  sheet: TimesheetRows
  /**
   * Ingebed onder een scherm dat zelf al navigeert. De eigen navigatie en grafiek
   * vervallen dan; deze kop komt ervoor in de plaats, op één regel met de acties.
   */
  embeddedHeading?: ReactNode
}

function isFreeDay(info: DayTotal | undefined): boolean {
  return info?.dayType === 'vacation' || info?.dayType === 'holiday'
}

export function WeekGrid({ weekId, onWeekChange, personId, me, editable, viewControl, actions, sheet, embeddedHeading }: WeekGridProps) {
  const embedded = embeddedHeading !== undefined
  const meta = weekMetaFromId(weekId)
  const weekQuery = useWeek(weekId, personId)
  const weekTotalsQuery = useWeekTotals(weekId, personId, !embedded)
  const pickersQuery = usePickers(personId)
  const saveCell = useSaveCell(personId, me)
  const exceptionsQuery = useWorkdayExceptions(meta.days[0].iso, meta.days[6].iso, personId, editable)

  const [showWeekend, setShowWeekend] = useState(false)

  const week = weekQuery.data
  // Een afgesloten week is een slot: de server weigert mutaties tot de week is heropend,
  // dus de bewerk-affordances gaan hier op slot. Alleen de heropen-knop blijft bereikbaar.
  // Bij een deelafsluiting geldt dat per dag: alleen t/m de knipdatum.
  const weekClosed = week?.approval?.status === 'APPROVED'
  const dayClosed = (date: string) => closedOn(week?.approval, date)
  /**
   * Tijdens een wissel van week of persoon houdt react-query de vorige uitslag in
   * beeld zodat het grid niet knippert. Die regels horen dan bij een andere week
   * of iemand anders, en hun entry-id's dus ook: bewerken zou de wijziging op de
   * verkeerde regel laten landen. Kijken mag, typen niet.
   */
  const stale = weekQuery.isPlaceholderData
  const canEdit = editable && !weekClosed && !stale
  const totalsByDay = new Map<number, DayTotal>((week?.dailyTotals ?? []).map((d) => [d.dayIndex, d]))
  const exceptionByDate = new Map<string, WorkdayException>((exceptionsQuery.data ?? []).map((e) => [e.date, e]))
  const weekendActive = meta.days.some((d) => !d.isWorkday && toNumber(totalsByDay.get(d.dayIndex)?.total) > 0)
  const visibleDays = meta.days.filter((d) => d.isWorkday || showWeekend || weekendActive)

  const { rows, savedRowKeys, landedRowKey, addRow, dropRow, replaceRow, alignRow, markRowSaved, clearLanded } = sheet

  // Het weekrooster toont alle zeven dagen naast elkaar, dus een nieuwe regel hoort hier
  // niet bij een dag: hij blijft staan tot je hem ergens invult.
  const addWeekRow = (selection: NewRowSelection) => addRow(selection, null)

  // De nieuwe regel opent op de dag van vandaag als die in deze week valt, anders op maandag.
  const todayColumn = Math.max(
    0,
    visibleDays.findIndex((d) => d.date.toDateString() === new Date().toDateString()),
  )
  const landedIndex = rows.findIndex((row) => row.rowKey === landedRowKey)
  // biome-ignore lint/correctness/useExhaustiveDependencies: landedRowKey blijft in de lijst zodat een volgende landing op dezelfde index de focus en de timer opnieuw zet
  useEffect(() => {
    if (landedIndex < 0) return
    document.getElementById(`cell-${landedIndex}-${todayColumn}`)?.focus()
    const timer = window.setTimeout(clearLanded, 900)
    return () => window.clearTimeout(timer)
  }, [landedIndex, landedRowKey, todayColumn, clearLanded])

  function handleGridKeyDown(event: KeyboardEvent<HTMLTableElement>) {
    const target = event.target as HTMLElement
    const attr = target.getAttribute?.('data-cell')
    if (!attr) return
    const match = /^cell-(\d+)-(\d+)$/.exec(attr)
    if (!match) return
    let row = Number(match[1])
    let col = Number(match[2])
    switch (event.key) {
      case 'ArrowRight':
        col += 1
        break
      case 'ArrowLeft':
        col -= 1
        break
      case 'ArrowDown':
        row += 1
        break
      case 'ArrowUp':
        row -= 1
        break
      default:
        return
    }
    const next = document.getElementById(`cell-${row}-${col}`)
    if (next) {
      event.preventDefault()
      next.focus()
    }
  }

  function saveCellValues(row: WeekRow, dayIndex: number, date: string, entryId: string | null, values: CellValues) {
    // De server zet een nieuwe boeking op de eerstvolgende plek van die dag; een verse
    // regel die een ander slot raadde hoort daar eerst naartoe.
    const rowKey = entryId === null ? alignRow(row.rowKey, dayIndex) : row.rowKey
    // Vanaf nu bestaat er invoer voor deze regel; een rijwissel moet naar de server.
    markRowSaved(rowKey)
    saveCell.mutate({
      rowKey,
      dayIndex,
      date,
      projectId: row.projectId,
      categoryId: row.categoryId,
      entryId,
      values,
    })
  }

  const todayStr = new Date().toDateString()
  const isToday = (date: Date) => date.toDateString() === todayStr

  const entryPanel = pickersQuery.data ? (
    <AddRowButton
      pickers={pickersQuery.data}
      onAdd={addWeekRow}
      variant={rows.length === 0 ? 'hero' : 'inline'}
    />
  ) : (
    <span className="text-sm text-ink-faint">Projecten laden…</span>
  )

  return (
    <div className="space-y-4">
      <TimesheetToolbar
        nav={
          embedded ? (
            embeddedHeading
          ) : (
            <>
              <WeekNav weekId={weekId} onChange={onWeekChange} />
              {viewControl}
            </>
          )
        }
        readout={
          embedded ? null : (
            <>
              {/* Wijkt als eerste bij krappe breedte: de weekbalk draagt hier ook de acties,
                  en het weeknummer plus de kalender benoemen de periode al. */}
              <span className="hidden text-sm text-ink-faint min-[1400px]:inline">{meta.rangeShort}</span>
              {weekTotalsQuery.data?.weeks?.length ? (
                <WeekTotalsStrip totals={weekTotalsQuery.data.weeks} weekId={weekId} onChange={onWeekChange} />
              ) : null}
            </>
          )
        }
        actions={
          <>
            {actions}
            {editable ? (
              <label className="flex cursor-pointer items-center gap-1.5 text-sm text-ink-soft">
                <input
                  type="checkbox"
                  checked={showWeekend || weekendActive}
                  disabled={weekendActive}
                  onChange={(event) => setShowWeekend(event.target.checked)}
                  className="h-3.5 w-3.5 accent-accent-600"
                />
                Weekend
              </label>
            ) : null}
            {week && !stale ? (
              <WeekApprovalControl
                approval={week.approval}
                weekId={weekId}
                personId={personId}
                editable={editable}
              />
            ) : null}
          </>
        }
      />

      {week?.utilization ? (
        <UtilizationStrip
          personId={personId}
          weekId={weekId}
          utilization={week.utilization}
          dailyTotals={week.dailyTotals}
        />
      ) : null}

      <div className="overflow-hidden rounded-card bg-surface shadow-card">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse text-sm" onKeyDown={handleGridKeyDown}>
            <thead>
              <tr className="border-b border-hairline">
                <th className="label-caps sticky left-0 z-10 bg-surface px-4 py-3 text-left">Klant · Project · Categorie</th>
                {visibleDays.map((day) => {
                  const info = totalsByDay.get(day.dayIndex)
                  const freeDay = isFreeDay(info)
                  return (
                    <th
                      key={day.dayIndex}
                      className={cn(
                        'px-2 py-2 text-center align-top font-medium',
                        isToday(day.date) ? 'bg-accent-600/5 text-accent-700' : freeDay && 'bg-fill-2/60',
                      )}
                    >
                      <DayHeaderPopover
                        weekId={weekId}
                        personId={personId}
                        date={day.iso}
                        short={day.short}
                        dayNumber={day.date.getDate()}
                        isToday={isToday(day.date)}
                        dayType={info?.dayType ?? (day.isWorkday ? 'workday' : 'weekend')}
                        dayLabel={info?.dayLabel ?? null}
                        exception={exceptionByDate.get(day.iso) ?? null}
                        editable={canEdit && !dayClosed(day.iso)}
                      />
                      {freeDay ? (
                        <span className="mt-0.5 flex items-center justify-center gap-1 text-[10px] font-medium uppercase tracking-wide text-ink-faint">
                          <Palmtree className="h-3 w-3" />
                          {info?.dayType === 'vacation' ? 'Vakantie' : info?.dayLabel ?? 'Feestdag'}
                        </span>
                      ) : null}
                    </th>
                  )
                })}
                <th className="label-caps px-4 py-3 text-right">Week</th>
                <th className="w-16 px-2 py-3" />
              </tr>
            </thead>
            <tbody>
              {weekQuery.isLoading ? (
                <SkeletonRows columns={visibleDays.length} />
              ) : rows.length === 0 ? (
                // De kolomkoppen blijven staan: daar zitten de dagacties (vakantie,
                // feestdag) die ook in een lege week bereikbaar moeten zijn.
                <tr>
                  <td colSpan={visibleDays.length + 3} className="px-4 py-6">
                    <EmptyState
                      icon={Clock}
                      title="Nog geen uren deze week"
                      description="Kies waar je aan werkte."
                      action={canEdit ? entryPanel : undefined}
                      className="rounded-none border-0 bg-transparent py-6"
                    />
                  </td>
                </tr>
              ) : (
                rows.map((row, rowIdx) => {
                  const pickerData = pickersQuery.data
                  const menu = rowMenuState(row, canEdit, pickerData, savedRowKeys.has(row.rowKey))
                  const { firstOfClient, isContinuation } = rowBoundary(row, rows[rowIdx - 1])
                  return (
                  <tr
                    key={row.rowKey}
                    className={cn(
                      'group border-b border-hairline transition-colors duration-700 ease-spring',
                      firstOfClient && rowIdx > 0 && 'border-t border-hairline-strong',
                      row.rowKey === landedRowKey && 'bg-accent-600/6',
                    )}
                  >
                    <td
                      className={cn(
                        'sticky left-0 z-10 px-4 py-2.5',
                        row.rowKey === landedRowKey
                          ? 'bg-[linear-gradient(rgb(var(--accent-600-rgb)/0.06),rgb(var(--accent-600-rgb)/0.06)),rgb(var(--surface-rgb))]'
                          : 'bg-surface',
                      )}
                    >
                      <RowLabelEditable
                        row={row}
                        firstOfClient={firstOfClient}
                        isContinuation={isContinuation}
                        menu={menu}
                        pickers={pickerData}
                        weekId={weekId}
                        personId={personId}
                        onLocalCommit={(selection) => replaceRow(row.rowKey, selection, null)}
                      />
                    </td>
                    {visibleDays.map((day, colIdx) => {
                      const cell = row.cells[day.dayIndex]
                      return (
                        <td
                          key={day.dayIndex}
                          className={cn(
                            'px-1.5 py-1.5',
                            isToday(day.date) ? 'bg-accent-600/4' : isFreeDay(totalsByDay.get(day.dayIndex)) && 'bg-fill-2/60',
                          )}
                        >
                          <CellPopover
                            row={row}
                            cell={cell}
                            editable={canEdit && !dayClosed(cell.date)}
                            readOnlyNote={
                              weekClosed ? 'Week afgesloten' : dayClosed(cell.date) ? 'Dag afgesloten' : null
                            }
                            cellId={`cell-${rowIdx}-${colIdx}`}
                            otherHoursOnDay={toNumber(totalsByDay.get(day.dayIndex)?.total) - toNumber(cell.hours)}
                            onSave={(values) => saveCellValues(row, day.dayIndex, cell.date, cell.entryId, values)}
                          />
                        </td>
                      )
                    })}
                    <td className="num px-4 py-2.5 text-right font-medium text-ink">
                      {formatHmm(toNumber(row.rowTotal))}
                    </td>
                    <td className="px-2 py-2.5">
                      {canEdit && !row.cells.some((c) => c.entryId !== null && dayClosed(c.date)) ? (
                        <RowActions
                          row={row}
                          weekId={weekId}
                          personId={personId}
                          saved={savedRowKeys.has(row.rowKey)}
                          onCopyRow={() => addWeekRow(selectionFromRow(row))}
                          onAddRow={addWeekRow}
                          pickers={pickersQuery.data}
                          onDropLocalRow={() => dropRow(row.rowKey)}
                        />
                      ) : null}
                    </td>
                  </tr>
                  )
                })
              )}
            </tbody>
            <tfoot>
              <tr className="border-t border-hairline-strong bg-fill-1 text-sm">
                <td className="sticky left-0 z-10 bg-surface-2 px-4 py-3 font-medium text-ink">Dagtotaal</td>
                {visibleDays.map((day) => {
                  const info = totalsByDay.get(day.dayIndex)
                  const total = toNumber(info?.total)
                  const expected = info?.expectedWorkday ?? day.isWorkday
                  const freeDay = isFreeDay(info)
                  return (
                    <td
                      key={day.dayIndex}
                      className={cn(
                        'px-2 py-3 text-center',
                        isToday(day.date) ? 'bg-accent-600/4' : freeDay && 'bg-fill-2/60',
                      )}
                    >
                      <div className={cn('num', total > 0 ? 'text-ink' : 'text-ink-faint/60')}>
                        {formatHmm(total)}
                      </div>
                      <div className="mt-1.5 flex justify-center">
                        {freeDay && total === 0 ? (
                          <Palmtree className="h-3.5 w-3.5 text-ink-faint" aria-label={info?.dayType === 'vacation' ? 'Vakantiedag' : 'Feestdag'} />
                        ) : (
                          <StoplichtDot status={dayStatus(total, expected)} />
                        )}
                      </div>
                    </td>
                  )
                })}
                <td className="num px-4 py-3 text-right font-semibold text-ink">
                  {formatHmm(toNumber(week?.weekTotal))}
                </td>
                <td className="px-2 py-3" />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {canEdit && !weekQuery.isLoading && rows.length > 0 ? entryPanel : null}
      {editable && weekClosed ? (
        <p className="text-sm text-ink-faint">Week is afgesloten - heropen de week om uren te wijzigen.</p>
      ) : editable && week?.approval?.approvedThrough ? (
        <p className="text-sm text-ink-faint">
          De dagen t/m {formatDayLabel(week.approval.approvedThrough)} zijn met de maand afgesloten - heropen de week om
          ze te wijzigen.
        </p>
      ) : null}

      <Legend />
    </div>
  )
}

function SkeletonRows({ columns }: { columns: number }) {
  return (
    <>
      {Array.from({ length: 3 }).map((_, r) => (
        <tr key={r} className="border-b border-hairline">
          <td className="px-4 py-3">
            <Skeleton className="h-4 w-40" />
          </td>
          {Array.from({ length: columns }).map((_, c) => (
            <td key={c} className="px-2 py-3">
              <Skeleton className="mx-auto h-6 w-10" />
            </td>
          ))}
          <td className="px-4 py-3">
            <Skeleton className="ml-auto h-4 w-10" />
          </td>
        </tr>
      ))}
    </>
  )
}

